import classNames from 'classnames';
import React, { useEffect } from 'react';
import Sprite from '../../assets/icons/sprite.svg';
import Button from '../../components/Button/Button';
import Overlay from '../../components/Overlay/Overlay';
import Portal from '../../components/Portal/Portal';
import Transition from '../../components/Transition/Transition';
import Position from '../../types/enum/Position';
import Size from '../../types/enum/Size';
import './Dialog.scss';
import IDialogProps from './DialogProps';

const Dialog = ({
  isOpen,
  onClose,
  overlay,
  allowOverlayClose,
  hasDismiss,
  size,
  layer,
  position,
  children,
  buttons,
  dialogRef,
  className,
}: IDialogProps) => {
  useEffect(() => {
    updateFreeze(isOpen);
    return () => updateFreeze(false);
  }, [isOpen]);

  // freeze/unfreeze body scroll
  function updateFreeze(shouldFreeze: boolean) {
    const { body } = document;
    if (body !== null) {
      if (shouldFreeze) {
        body.classList.add('dialog-freeze-scroll-' + layer);
      } else {
        body.classList.remove('dialog-freeze-scroll-' + layer);
      }
    }
  }

  function handleOverlayClick() {
    if (allowOverlayClose && onClose) {
      onClose();
    }
  }

  function RenderedButtons() {
    return (
      <div className="dialog-buttons">
        {buttons}
        {hasDismiss && renderDismissButton()}
      </div>
    );
  }

  function renderDismissButton() {
    return (
      <Button size={Size.SMALL} type="icon" onClick={onClose}>
        <svg className="icon" width="24px" height="24px">
          <use xlinkHref={`${Sprite}#close`} />
        </svg>
      </Button>
    );
  }

  function setRef(elt: HTMLDivElement | null) {
    if (dialogRef) {
      dialogRef(elt);
    }
  }

  const classes = classNames(
    {
      'dialog-shell': true,
      'dialog-shell-full': size === 'full',
      [`dialog-position-center`]: position === 'center',
    },
    className
  );

  return (
    <Portal>
      <Transition isOpen={isOpen} transitionName="dialog-anim">
        <div className={classes} style={{ zIndex: 1000 + (layer ? layer : 0) }}>
          {overlay && <Overlay onClick={handleOverlayClick} />}
          <div className={`dialog dialog-${size}`} ref={setRef}>
            <RenderedButtons />
            {children}
          </div>
        </div>
      </Transition>
    </Portal>
  );
};

Dialog.defaultProps = {
  overlay: true,
  allowOverlayClose: true,
  hasDismiss: true,
  size: Size.MEDIUM,
  layer: 1,
  position: Position.TOP,
};

export default Dialog;
